import { useCallback, useState } from 'react';
import type {
  ServerMessage,
  GameStartPayload,
  BoardUpdatePayload,
  GameEndPayload,
  TurnStartPayload,
  OpponentLeftPayload,
  ErrorPayload,
} from './useWebSocket';

// ─── Types ──────────────────────────────────────────────────────────────────

/** A single cell on the board: player's avatar string or null if empty */
export type CellValue = string | null;

/** The 9-cell board state (indices 0-8, left-to-right, top-to-bottom) */
export type Board = [
  CellValue, CellValue, CellValue,
  CellValue, CellValue, CellValue,
  CellValue, CellValue, CellValue,
];

/** Scores from the local player's point of view */
export type Scores = { you: number; opponent: number };

/** Game result outcomes */
export type GameResultType = 'win' | 'lose' | 'draw' | 'forfeit' | 'resign';

/** Full game result when a game ends */
export interface GameResult {
  result: GameResultType;
  winner?: string;
  loser?: string;
}

/** Information about the opponent */
export interface OpponentInfo {
  nickname: string;
  avatar: string;
}

/** Error received from the server */
export interface GameError {
  code: string;
  message: string;
}

/** Internal state managed by the hook */
export interface GameState {
  board: Board;
  isMyTurn: boolean;
  myAvatar: string | null;
  scores: Scores;
  seriesScores: Scores;
  gameResult: GameResult | null;
  opponentInfo: OpponentInfo | null;
  lastMove: number | null;
  winningCells: number[] | null;
  opponentLeft: boolean;
  opponentLeftReason: string | null;
  error: GameError | null;
}

/** Public return type of the useGameState hook */
export interface UseGameStateReturn {
  board: Board;
  scores: Scores;
  seriesScores: Scores;
  gameResult: GameResult | null;
  isMyTurn: boolean;
  myAvatar: string | null;
  opponentInfo: OpponentInfo | null;
  lastMove: number | null;
  winningCells: number[] | null;
  opponentLeft: boolean;
  opponentLeftReason: string | null;
  error: GameError | null;
  processMessage: (message: ServerMessage) => void;
  applyOptimisticMove: (cell: number) => void;
  resetGame: () => void;
  resetSeries: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_BOARD: Board = [null, null, null, null, null, null, null, null, null];

const ZERO_SCORES: Scores = { you: 0, opponent: 0 };

const INITIAL_STATE: GameState = {
  board: EMPTY_BOARD,
  isMyTurn: false,
  myAvatar: null,
  scores: ZERO_SCORES,
  seriesScores: ZERO_SCORES,
  gameResult: null,
  opponentInfo: null,
  lastMove: null,
  winningCells: null,
  opponentLeft: false,
  opponentLeftReason: null,
  error: null,
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useGameState(): UseGameStateReturn {
  const [state, setState] = useState<GameState>(INITIAL_STATE);

  /** Process an incoming WebSocket server message */
  const processMessage = useCallback((message: ServerMessage) => {
    switch (message.event) {
      case 'game_start': {
        const payload = message.payload as GameStartPayload;
        setState((prev) => ({
          ...prev,
          board: (payload.board?.slice(0, 9) ?? EMPTY_BOARD) as Board,
          isMyTurn: Boolean(payload.isYourTurn),
          myAvatar: payload.myAvatar ?? prev.myAvatar,
          scores: prev.seriesScores,
          gameResult: null,
          winningCells: null,
          opponentInfo: {
            nickname: payload.opponent.nickname,
            avatar: payload.opponent.avatar,
          },
          lastMove: null,
          opponentLeft: false,
          opponentLeftReason: null,
          error: null,
        }));
        break;
      }

      case 'board_update': {
        const payload = message.payload as BoardUpdatePayload;
        setState((prev) => ({
          ...prev,
          board: payload.board.slice(0, 9) as Board,
          isMyTurn: Boolean(payload.isYourTurn),
          scores: payload.scores ?? prev.scores,
          lastMove: payload.lastMove,
          error: null,
        }));
        break;
      }

      case 'game_end': {
        const payload = message.payload as GameEndPayload;
        setState((prev) => ({
          ...prev,
          board: (payload.board?.slice(0, 9) ?? prev.board) as Board,
          isMyTurn: false,
          winningCells: payload.winningCells ?? null,
          gameResult: { result: payload.result },
          scores: payload.scores ?? prev.scores,
          seriesScores: payload.scores ?? prev.seriesScores,
        }));
        break;
      }

      case 'turn_start': {
        const payload = message.payload as TurnStartPayload;
        setState((prev) => ({
          ...prev,
          isMyTurn: Boolean(payload.isYourTurn),
          error: null,
        }));
        break;
      }

      case 'opponent_left': {
        const payload = message.payload as OpponentLeftPayload;
        setState((prev) => ({
          ...prev,
          opponentLeft: true,
          opponentLeftReason: payload.reason,
        }));
        break;
      }

      case 'error': {
        const payload = message.payload as ErrorPayload;
        setState((prev) => ({
          ...prev,
          error: { code: payload.code, message: payload.message },
        }));
        break;
      }

      default:
        // Ignore unhandled events (e.g. seq_ack)
        break;
    }
  }, []);

  /** Optimistically place the local player's move before the server confirms.
   *  The subsequent board_update reconciles (and will correct this if the
   *  server rejects the move). Flips isMyTurn off to prevent double-moves. */
  const applyOptimisticMove = useCallback((cell: number) => {
    setState((prev) => {
      if (prev.board[cell] !== null || !prev.myAvatar) return prev;
      const board = [...prev.board] as Board;
      board[cell] = prev.myAvatar;
      return { ...prev, board, isMyTurn: false, lastMove: cell };
    });
  }, []);

  /** Reset the game state for "Play Again" — preserves series scores and opponent info */
  const resetGame = useCallback(() => {
    setState((prev) => ({
      ...INITIAL_STATE,
      opponentInfo: prev.opponentInfo,
      scores: prev.seriesScores,
      seriesScores: prev.seriesScores,
    }));
  }, []);

  /** Reset the entire series — used when player exits to lobby */
  const resetSeries = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    board: state.board,
    scores: state.scores,
    seriesScores: state.seriesScores,
    gameResult: state.gameResult,
    isMyTurn: state.isMyTurn,
    myAvatar: state.myAvatar,
    opponentInfo: state.opponentInfo,
    lastMove: state.lastMove,
    winningCells: state.winningCells,
    opponentLeft: state.opponentLeft,
    opponentLeftReason: state.opponentLeftReason,
    error: state.error,
    processMessage,
    applyOptimisticMove,
    resetGame,
    resetSeries,
  };
}
